import { ArrowsClockwise, DotsThreeVertical } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { timeSince } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadListUpdateStatus from "../../hooks/useLeadListUpdateStatus";
import { isActionsElementClick } from "../../utils";
import LeadListActions from "../LeadListActions";
import LeadListSourceTypes from "./LeadListSourceTypes";

import type { LeadList } from "../../types";

interface LeadListItemProps {
  leadList: LeadList;
}

export default function LeadListItem({ leadList }: LeadListItemProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id, name, lead_count: leadCount, created_at: createdAt } = leadList;

  const { isUpdating, lastUpdatedAt } = useLeadListUpdateStatus({ listId: id });

  return (
    <Link
      to={id}
      state={{ leadList }}
      onClick={(e) => {
        if (isActionsElementClick(e.target)) {
          e.preventDefault();
        }
      }}
      className="group flex cursor-pointer hover:shadow-sm transition-shadow duration-300 flex-col gap-4 border border-black-200 rounded-5xl p-4"
    >
      {/* List sources */}
      <div className="flex flex-row gap-4 justify-between items-center">
        <LeadListSourceTypes listId={id} />

        <LeadListActions leadList={leadList}>
          <Button
            asChild
            intent="iconOnly"
            variant={isDesktop ? "ghost-black" : "outline-black"}
            size={isDesktop ? "xs" : "sm"}
            className={clsx([
              isDesktop &&
                "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity duration-300",
            ])}
          >
            {/** biome-ignore lint/a11y/useSemanticElements: this "button" has to be a div since it can't be nested inside the parent Link */}
            <div role="button" tabIndex={0} aria-label="More options">
              <DotsThreeVertical />
            </div>
          </Button>
        </LeadListActions>
      </div>

      <div className="flex flex-col">
        {/* List name and creation date */}
        <div className="flex flex-col gap-1">
          <span className="text-md-bold truncate" title={name}>
            {name}
          </span>
          <span className="text-button-xxs text-black-400">
            Created on{" "}
            {new Date(createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>

        {/* Lead count */}
        <div className="flex flex-row items-end">
          {isUpdating ? (
            <Tag
              variant="tertiary"
              size="sm"
              className="w-fit"
              leftIcon={<ArrowsClockwise className="text-green-500" />}
            >
              Updating
            </Tag>
          ) : (
            <RenderIf condition={!!lastUpdatedAt}>
              <Tag variant="quaternary" size="sm">
                {timeSince(new Date(lastUpdatedAt).getTime())}
              </Tag>
            </RenderIf>
          )}

          <div className="flex flex-col items-end ml-auto">
            <span className="text-md-bold">{leadCount}</span>
            <span className="text-button-xxs text-black-400">leads</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
